import { useState } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { cn } from '@rocco/utils/cn';
import ProductTagsDisplay from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import ProductTaggingShell from '@modules/product-tagging/components/product-tagging-shell/ProductTaggingShell';
import { useProductTagging } from '@modules/product-tagging/context';
import TagPopover from '@modules/page-builder/component/detail-article/tag-popover/TagPopover';
import HoverButtonsImage from '@modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import { getImageAttrs } from '~/modules/images';
import { REVAMP_IMAGE_COLUMN_SIZES } from '../../util/revamp-image-sizes';
import { describeImage } from '../../util/describe-image';
import type { ImageColumnProps } from '@modules/page-builder/types';

interface RevampImageFigureProps {
    column: ImageColumnProps;
    /** The figure's rendered width as a `sizes` string; defaults to the 900 body column. */
    sizes?: string;
    className?: string;
    imgClassName?: string;
    captionClassName?: string;
    /** 1-based figure number rendered as the "( N )" caption prefix. */
    number?: number;
}

/**
 * Shared image + caption figure for the revamp image modules — the image at
 * its column width with the small numbered caption below (Figma "Description",
 * 13.9px at 80% ink, "( N )" figure prefix), carrying the article's product
 * tags and the legacy photo module's hover actions. The image-with-CTA-buttons
 * design is a separate page-builder module to come (BC-4720 follow-up) so
 * editors configure buttons per instance.
 */
export const RevampImageFigure = ({
    column,
    sizes = REVAMP_IMAGE_COLUMN_SIZES,
    className,
    imgClassName,
    captionClassName,
    number,
}: RevampImageFigureProps) => {
    const { mobile } = useAppDisplayModeContext();
    const { canCreateTag } = useProductTagging();
    // Same experiment gate as the legacy article image shell, so the revamp
    // doesn't silently take the dots to 100% exposure. Taggers are excluded so
    // the dots stay clear of the tag-authoring overlay, as legacy does.
    const tagDotsEnabled = useABTestIsOn('product_tagging_dots');
    // The dots' expanded panel covers the same corner as the hover actions.
    const [showHoverButtons, setShowHoverButtons] = useState(true);

    if (!column.imageUrl) return null;

    const { width, height } = column.dimension ?? {};
    const caption = column.caption
        ? number
            ? `( ${number} ) ${column.caption}`
            : column.caption
        : '';
    // Desktop overlays positioned dots on the image; mobile lists the approved
    // tags under it — the same split (and the same two data sources) the legacy
    // article uses.
    const { libraryImage, tags, uniqueApprovedTags, dimension } = column;
    const tagDots =
        tagDotsEnabled && !canCreateTag && libraryImage && tags?.length
            ? { libraryImage, tags }
            : null;
    const tagList =
        mobile && uniqueApprovedTags?.length && dimension
            ? { tags: uniqueApprovedTags, dimension }
            : null;
    // Boosting rewrites the stored image, so it stays a desktop-only staff
    // action; the overlay itself renders nothing for everyone else.
    const canBoostImage = !mobile && !!libraryImage?.ID;

    const imageBox = (
        <div
            // The actions sit hidden on the image until it is hovered, the
            // reveal the legacy shell drives from its own container rule.
            // Important, because the buttons' resting `opacity: 0` is a fela
            // rule: fela injects unlayered CSS, which outranks anything in
            // Tailwind's utilities layer however specific it is.
            className="relative w-full [&:hover_.hover-buttons-image]:visible! [&:hover_.hover-buttons-image]:opacity-100!"
        >
            {/* Body images sit below the fold on every layout, so they load
                lazily; the hero is the only image on the critical path. */}
            <img
                {...getImageAttrs(column.imageUrl, { sizes })}
                alt={describeImage(column.caption, column.libraryImage?.Title)}
                loading="lazy"
                decoding="async"
                className={cn('w-full object-cover', imgClassName)}
                {...(width && height
                    ? {
                          width,
                          height,
                          style: { aspectRatio: `${width} / ${height}` },
                      }
                    : {})}
            />
            {/* The authoring surface behind the "Start tagging" action. */}
            {canCreateTag && libraryImage?.ID && (
                <ProductTaggingShell libraryImage={libraryImage} tags={tags} />
            )}
            {tagDots && (
                <ProductTagsDisplay
                    libraryImage={tagDots.libraryImage}
                    tags={tagDots.tags}
                    onToggle={(toggled) => setShowHoverButtons(!toggled)}
                />
            )}
            {libraryImage?.ID && showHoverButtons && (
                <HoverButtonsImage
                    photoID={libraryImage.ID}
                    libraryItemId={libraryImage.LibraryItemID ?? undefined}
                    clickableLink={libraryImage.ClickableLink || undefined}
                    imageURL={column.imageUrl}
                    downloadLink={libraryImage.DownloadLink || undefined}
                    taggingEnabled={canCreateTag}
                />
            )}
        </div>
    );

    return (
        <figure
            className={cn('m-0 flex w-full flex-col gap-4 md:gap-6', className)}
        >
            {canBoostImage && libraryImage?.ID ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImage.ID}
                    currentImageUrl={column.imageUrl}
                    displayWidth={width ?? 0}
                    displayHeight={height ?? 0}
                    columnId={column.id}
                    wasUpscaled={!!libraryImage.WasUpscaled}
                    className="w-full"
                >
                    {imageBox}
                </BoostResolutionOverlay>
            ) : (
                imageBox
            )}
            {tagList && (
                <TagPopover tags={tagList.tags} dimension={tagList.dimension} />
            )}
            {caption && (
                <figcaption
                    className={cn(
                        'm-0 font-sans text-[0.75rem] leading-[1.3] tracking-[-0.03em] text-black/80 md:w-[25rem] md:text-[0.87rem]',
                        captionClassName,
                    )}
                >
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};
